import { Contexts, getContainer, StorageError, StorageErrorType, User } from "@dandelion/core";
import axios from "axios";
import { isEqual } from "lodash";
import { injectable } from "inversify";
import { PreferenceValue } from "../entities/Preference";
import { PreferencesStorageInterface } from "../interfaces/PreferencesStorageInterface";
import { UserProvider } from "../services/UserProvider";
import { Preferences } from "../enum/Preferences";

@injectable()
export class RESTPreferenceStorage implements PreferencesStorageInterface {
  protected readonly BASE_URL;
  protected currentUser: User;

  constructor(protected baseURL: string, protected defaultPreferences: PreferenceValue[], protected userProvider: UserProvider) {
    this.BASE_URL = import.meta.env.VITE_APP_API_BASE_URL + this.baseURL;
    axios.interceptors.request.use((config: any) => {
      config.headers = { "Authorization": `Bearer ${window.localStorage.getItem('jwtToken')}` };
      return config;
    });
    const token = this.userProvider.getToken();
    this.currentUser = this.userProvider.verifyToken(token!).currentUser;
  }

  async getPreference(name: string): Promise<PreferenceValue> {
    const preferenceValues = await this.getPreferences();
    const preferenceValue = preferenceValues.find((prefValue) => prefValue.preferenceName === name);

    if (preferenceValue) {
      return preferenceValue;
    }

    const defaultPreference = this.defaultPreferences.find(
      (prefValue) => prefValue.preferenceName === name
    );

    if (defaultPreference) {

      await this.savePreference(defaultPreference);
      return defaultPreference;
    }
const preference = Preferences[name as keyof typeof Preferences];

if (!preference) {
    const preference = Object.values(Preferences).includes(name as Preferences)
  ? (name as Preferences)
  : undefined;
  if(!preference)
  throw new Error(`Invalid preference: ${name}`);
}
    return {
      preferenceName: preference,
      value: '',
    };
  }

  async getPreferences(): Promise<Array<PreferenceValue>> {
    try {
      const centreCode = this.currentUser.centre || '*';
      const username = this.currentUser.username;
      const response = this.baseURL == 'UsersData/' ? await axios.get(`${this.BASE_URL}getUserPreferences/${centreCode}/${username}/`)
        : await axios.get(`${this.BASE_URL}get/${centreCode}/`);
      let userPreferences = response.data;
      if (isEqual(userPreferences, {})) {
        await this.saveAllPreferences(this.defaultPreferences);
        userPreferences = this.defaultPreferences;
      }
      return userPreferences;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      throw error;
    }
  }

  async saveAllPreferences(preferenceValues: Array<PreferenceValue>): Promise<void> {
    const centreCode = this.currentUser.centre || '*';
    const username = this.currentUser.username;

    if (this.baseURL === 'UsersData/') {
      await axios.post(
        `${this.BASE_URL}savePreferences/${centreCode}/${username}/`,
        preferenceValues
      );
    } else {
      await axios.post(`${this.BASE_URL}save/${centreCode}/`, preferenceValues);
    }
  }

  async savePreference(newPreference: PreferenceValue): Promise<void> {
    const preferenceValues = await this.getPreferences();
    const index = preferenceValues.findIndex(
      (prefValue) => prefValue.preferenceName === newPreference.preferenceName
    );

    if (index !== -1) {
      preferenceValues[index].value = newPreference.value;
    } else {
      preferenceValues.push(newPreference);
    }

    await this.saveAllPreferences(preferenceValues);
  }
}
